import { useDemoSafeMutation } from '@/hooks/useDemoGuard';
import { notificationsRepository } from '@/repositories/notifications';
import {
  setCompanyHatifCredentials,
  type SetCompanyHatifCredentialsRequest,
} from '@/services/api';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useAuth } from './useAuth';
import { useSuperAdmin } from './useSuperAdmin';

// Non-secret status of a company's Hatif credentials, from the
// get_company_hatif_status RPC (super-admin-guarded in SQL). The secret itself
// is never returned — only whether it's configured.
export interface CompanyHatifCredentialsStatus {
  clientId: string | null;
  whatsappChannelId: string | null;
  /** Voxa workspace UUID — Nest reads this off inbound webhooks to attach
   * the right company_id to the audit row (RLS + Realtime depend on it). */
  workspaceId: string | null;
  /** WhatsApp Business Account id holding this company's templates — the live
   * templates view reads from it. */
  wabaId: string | null;
  scope: string | null;
  baseUrl: string | null;
  enabled: boolean;
  configured: boolean;
  updatedAt: string | null;
}

export function useCompanyHatifCredentials(companyId: string | null) {
  const { user } = useAuth();
  const { isSuperAdmin } = useSuperAdmin();
  const queryClient = useQueryClient();
  const queryKey = ['company-hatif-status', companyId];

  const query = useQuery({
    queryKey,
    queryFn: async (): Promise<CompanyHatifCredentialsStatus | null> => {
      if (!companyId) return null;
      const row =
        await notificationsRepository.getCompanyHatifStatus(companyId);
      if (!row) return null;
      return {
        clientId: row.client_id,
        whatsappChannelId: row.whatsapp_channel_id,
        workspaceId: row.workspace_id ?? null,
        wabaId: row.waba_id ?? null,
        scope: row.scope,
        baseUrl: row.base_url,
        enabled: row.enabled,
        configured: row.configured,
        updatedAt: row.updated_at,
      };
    },
    // get_company_hatif_status is super-admin-guarded in SQL; firing it for
    // non-super-admins returns 'not authorized' which Sentry captures as noise.
    enabled: !!companyId && isSuperAdmin,
    retry: false,
  });

  const save = useDemoSafeMutation({
    mutationFn: async (
      input: Omit<SetCompanyHatifCredentialsRequest, 'companyId' | 'updatedBy'>
    ) => {
      if (!companyId) throw new Error('no company selected');
      // Defense-in-depth mirror of the read gate above — the Nest endpoint is
      // the structural backstop; this keeps a non-super-admin misfire from
      // ever leaving the client.
      // Bilingual because CompanyChannelCredentials interpolates err.message
      // into its t()-wrapped failure toast — this string renders inside the
      // toast body in either UI language.
      if (!isSuperAdmin) throw new Error('غير مصرح — not authorized');
      await setCompanyHatifCredentials({
        ...input,
        companyId,
        updatedBy: user?.id,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey });
      queryClient.invalidateQueries({
        queryKey: ['company-available-channels'],
      });
    },
  });

  return { ...query, save };
}
